import { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useVersion } from 'react-admin';
import { Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SnackbarContainer } from 'components/standalone';
import { extractErrorMessage } from 'utils';
import { BoardContainer, BoardFormValues } from '../containers';
import { BoardForm } from '../components';

const BoardEditInner = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();

  const {
    selectedBoard,
    isLoading,
    boardLoaded,
    boardError,
    formLoading,
    setFormLoading,
    loadBoardById,
    updateBoard,
    existingBoards,
    loadBoardTypesForValidation,
  } = BoardContainer.useContainer();

  // BACKWARDS COMPAT: useVersion triggers re-fetch when react-admin
  // toolbar refresh button is clicked. Required while using custom
  // containers instead of react-admin's built-in data hooks.
  const version = useVersion();

  useEffect(() => {
    if (!id) return;
    loadBoardById(id);
  }, [id, version, loadBoardById]);

  useEffect(() => {
    loadBoardTypesForValidation();
  }, [version, loadBoardTypesForValidation]);

  useEffect(() => {
    if (boardError || (boardLoaded && !selectedBoard)) {
      notify('Element does not exist', 'error');
      history.push('/board-type');
    }
  }, [boardError, boardLoaded, selectedBoard, notify, history]);

  const handleSubmit = useCallback(
    async (values: BoardFormValues) => {
      if (!id) return;

      setFormLoading(true);
      try {
        const { name, shortName, externalId, payableResourceTypes } = values;
        await updateBoard(id, {
          name,
          shortName,
          externalId,
          payableResourceTypes,
        });
        notify('Element updated');
        history.push('/board-type');
      } catch (error: unknown) {
        console.error('Board update failed:', error);
        notify(extractErrorMessage(error, 'Failed to update board'), 'error');
      } finally {
        setFormLoading(false);
      }
    },
    [id, updateBoard, history, notify, setFormLoading],
  );

  const handleGoBack = useCallback(() => {
    history.push('/board-type');
  }, [history]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedBoard) {
    return null;
  }

  return (
    <Box className={classes.formContainer}>
      <BoardForm
        mode="edit"
        initialValues={selectedBoard}
        existingBoards={existingBoards}
        currentBoardId={id}
        onSubmit={handleSubmit}
        loading={formLoading}
        onGoBack={handleGoBack}
      />
    </Box>
  );
};

export const BoardEdit = () => (
  <BoardContainer.Provider>
    <BoardEditInner />
  </BoardContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default BoardEdit;
